import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Link,
  MenuItem,
  Paper,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import useJobs from "../../../hooks/useJobs";
import { useDebounce } from "use-debounce";
import { useInView } from "react-intersection-observer";
import InfiniteScrollerTable from "../../../components/InfiniteScroller/InfiniteScrollerTable";
import ListFooter from "../../../components/InfiniteScroller/ListFooter";
import TextFieldDebounced from "../../../components/TextFieldDebounced";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const ShopJobs = ({ search, status }) => {
  const { authToken, ezorder } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const navigate = useNavigate();
  const theme = useTheme();

  const [jobs, onRefresh, refreshing, onLoadMore, loadingMore, total, hasMore] =
    useJobs(authToken, 50, null, search, status);

  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0.5, //Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
  });

  const [inViewDebounce] = useDebounce(inView, 1000);

  const updateSearch = (searchUpdate, statusUpdate) => {
    let query = [];
    if (searchUpdate || statusUpdate) {
      if (searchUpdate) {
        query.push(`search=${searchUpdate}`);
      }
      if (statusUpdate) {
        query.push(`status=${statusUpdate}`);
      }
    }
    if (query.length > 0) {
      navigate(`/jobs?${query.join("&")}`); // search=hello&status=DRAFT
    } else {
      navigate(`/jobs`);
    }
  };

  useEffect(() => {
    if (jobs.length > 0 && inView) {
      console.log("Footer Element In View. Load More...");
      onLoadMore();
    }
  }, [inViewDebounce]);

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
    >
      <Typography variant="h4" component="div">
        Jobs
      </Typography>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: "16px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ marginLeft: "8px", flex: 1 }}>
          <TextFieldDebounced
            size="small"
            fullWidth
            variant="outlined"
            label="Search"
            value={search}
            onChange={(e) => {
              updateSearch(e.target.value, status);
            }}
          />
        </Box>
        <TextField
          size="small"
          sx={{ width: "120px", marginLeft: "8px" }}
          select
          label="Status"
          value={status}
          onChange={(e) => {
            updateSearch(search, e.target.value);
          }}
        >
          <MenuItem value={"OPEN"}>Open</MenuItem>
          <MenuItem value={"CLOSED"}>Closed</MenuItem>
          <MenuItem value={"EXPIRED"}>Expired</MenuItem>
          <MenuItem value={"ALL"}>All</MenuItem>
        </TextField>
      </Box>
      <InfiniteScrollerTable
        tableProps={{ size: "small" }}
        TableContainerComponent={Paper}
        TableStyle={{ minWidth: "600px" }}
        TableHead={() => (
          <TableHead>
            <TableRow>
              {/* <TableCell align="left">Date Created</TableCell> */}
              <TableCell align="left">Date Approved</TableCell>
              <TableCell align="left">Job No.</TableCell>
              <TableCell align="left">Project</TableCell>
              <TableCell align="left">Customer</TableCell>
            </TableRow>
          </TableHead>
        )}
        items={jobs}
        renderRow={(item, index) => {
          let scheduledSubPhases = 0;
          let totalSubPhases = 0;
          for (let phase of item.phases) {
            for (let subPhase of phase.subPhases) {
              totalSubPhases++;
              if (
                subPhase.calendarEvents &&
                subPhase.calendarEvents.length > 0
              ) {
                scheduledSubPhases++;
              }
            }
          }

          return (
            <TableRow
              key={item.id}
              hover
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                cursor: "pointer",
                background:
                  item.company.creditType === "Pay in Advance" ||
                  item.company.creditType === "Cash On Delivery"
                    ? "#ffecec"
                    : "inherit",
              }}
              onClick={() => {
                // Navigate to Job Page
                navigate(`/orders/${item.id}`);
              }}
            >
              <TableCell align="left">
                {dayjs(item.estimateApprovalDate)
                  .tz("America/Phoenix")
                  .format("MM/DD/YY")}
              </TableCell>
              <TableCell align="left">{item.id}</TableCell>
              <TableCell align="left">{item.project.name}</TableCell>
              <TableCell align="left">
                {item.company && item.company.name
                  ? item.company.name
                  : "GUEST"}
              </TableCell>
            </TableRow>
          );
        }}
        onRefresh={onRefresh}
        onEndReached={onLoadMore}
        loading={refreshing || loadingMore}
        ListFooterComponent={() => {
          return <ListFooter hasMore={hasMore} onClick={onLoadMore} />;
        }}
      />
    </Box>
  );
};
export default ShopJobs;
